import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {FlexColumn, Modal, Text, TextField} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import DeleteNovaAgentMutation from '../../../../mutations/nova/DeleteNovaAgentMutation';
import {toRelayGlobalId} from '../shared/relayGlobalId';

export interface DeleteNovaAgentModalProps {
	agentId: string;
	// The agent's slug. Shown in the title, and the exact string the author has to retype to unlock
	// the delete.
	agentName: string;
	// Fired after the delete succeeds, so the caller can clear the selection and refetch the
	// catalogue.
	onDeleted: () => void;
	// Fired when the author takes the reversible route instead — the caller switches to the Deploy
	// tab, where the audience can be emptied without destroying anything.
	onTurnOffInstead: () => void;
	onClose: () => void;
}

export function DeleteNovaAgentModal({
	agentId,
	agentName,
	onDeleted,
	onTurnOffInstead,
	onClose,
}: DeleteNovaAgentModalProps): JSX.Element {
	const {formatMessage} = useIntl();
	const [typedName, setTypedName] = useState('');
	const [deleting, setDeleting] = useState(false);
	const [deleteFailed, setDeleteFailed] = useState(false);

	// Exact match, deliberately case- and whitespace-sensitive. The gate exists because this destroys
	// every version of the agent irrecoverably, in a tool that acts on production data; a forgiving
	// comparison would defeat the point of asking.
	const isConfirmed = typedName === agentName;

	const handleConfirm = (): void => {
		if (deleting || !isConfirmed) {
			return;
		}
		setDeleting(true);
		setDeleteFailed(false);
		Util.CommitMutation(
			DeleteNovaAgentMutation,
			{id: toRelayGlobalId('NovaAgent', agentId)},
			() => {
				onDeleted();
				onClose();
			},
			true,
			null,
			(transaction, onFailure) => {
				// Call the fallback so the standard error modal still fires, and keep this modal open
				// with its own inline error so the author can retry without losing context. A true
				// return means the fallback scheduled a retry of this same mutation rather than
				// surfacing an error — the delete is still in flight, so stay in the deleting state and
				// let the retry's own outcome decide, instead of showing a failure over a delete that
				// may be about to succeed.
				if (onFailure(transaction) === true) {
					return;
				}
				setDeleting(false);
				setDeleteFailed(true);
			}
		);
	};

	const handleTurnOffInstead = (): void => {
		onTurnOffInstead();
		onClose();
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={onClose} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'nova.deleteAgent.title'}, {agentName})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'m'}>
					<Text data-cy="nova-delete-agent-message">
						{formatMessage({id: 'nova.deleteAgent.message'})}{' '}
						<b>{formatMessage({id: 'nova.deleteAgent.irreversible'})}</b>
					</Text>
					<TextField
						label={formatMessage({id: 'nova.deleteAgent.confirmLabel'}, {agentName})}
						data-cy="nova-delete-agent-confirm-input"
						value={typedName}
						onValueChange={setTypedName}
						width={TextField.WIDTH.FLEX}
					/>
					{deleteFailed && (
						<Text color="error" data-cy="nova-delete-agent-error">
							{formatMessage({id: 'nova.deleteAgent.submitFailed'})}
						</Text>
					)}
				</FlexColumn>
			</Modal.Content>
			{/* Child order is render order. The design system wraps a tertiary button in a flex-grow row
			    justified to the end, so "Turn it off instead" lands immediately left of Cancel rather
			    than flush against the modal's left edge as drawn — an accepted deviation, since that
			    alignment lives in the published design-system package and a dozen other footers depend
			    on it. The footer also accepts none but its own three button types, so there is no
			    hand-rolled alternative to reach for. */}
			<Modal.Footer>
				<Modal.TertiaryFooterButton onPress={handleTurnOffInstead} dataCy="nova-delete-agent-turn-off">
					{formatMessage({id: 'nova.deleteAgent.turnOffInstead'})}
				</Modal.TertiaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose} dataCy="nova-cancel-delete-agent">
					{formatMessage({id: 'nova.deleteAgent.cancelButton'})}
				</Modal.SecondaryFooterButton>
				<Modal.PrimaryFooterButton
					onPress={handleConfirm}
					disabled={!isConfirmed || deleting}
					dataCy="nova-confirm-delete-agent"
				>
					{formatMessage({id: 'nova.deleteAgent.confirmButton'})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
